import React, { useCallback, useEffect, useMemo } from 'react';

import {
    calculateHourMinFormat,
    formatDate,
    sortableHeader
} from 'pages/reports/components/ReportFunctions';
import { ReportToolTip } from 'pages/reports/components/StudentMetricsDisplay/StudentMetricsDisplay';
import { createSortChangeHandler, GridSortChangeEvent } from 'pages/reports/components/sortUtils';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { Card, Col, Container, Row, Table } from 'react-bootstrap';
import { orderBy, SortDescriptor } from 'utils/dataQuery';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { ReportSection } from 'apis/ReportApi';
import { buildExportFileName } from 'utils';
import { useSuperState } from 'hooks';
import { CSVLink } from 'react-csv';

import 'pages/reports/components/Report72/Report72.scss';

interface Report72Props {
    mode?: string;
    reportSections: ReportSection[];
}

interface ObjectiveOption {
    name: string;
    value: string;
}

interface QuestionRow {
    attempts: number;
    avgTimePerAttemptMinutes: number;
    displayName: string;
    highestScore: number | null;
    lastAttempt: Date | null;
    mostRecentScore: number | null;
    objectives: string[];
    QuestionId: number;
    totalTimeSpentMinutes: number;
}

interface SummaryMetrics {
    attained: number;
    avgAttemptsPerQuestion: number;
    coverage: number;
    recentCorrectness: number;
    totalAttempts: number;
    totalTimeSpentMinutes: number;
}

const MAX_DISPLAY_ROWS = 500;

/**
 * Format a score value for display.
 * Binary: 1 = correct, 0 = incorrect, null = "--"
 * Partial-credit: decimal between 0-1
 */
const formatScore = (score: number | null): string => {

    if (score === null) return '--';
    return score.toString();

};

export const Report72 = ({
    mode = 'Testing',
    reportSections
}: Report72Props) => {

    const [state, setState] = useSuperState({
        allQuestionRows: [] as QuestionRow[],
        exportFileName: 'StudentTestDetailReport.csv',
        exportRows: [] as any[],
        objectiveOptions: [] as ObjectiveOption[],
        selectedObjectives: [] as ObjectiveOption[],
        sort: [] as SortDescriptor[],
        summaryMetrics: {
            attained: 0,
            avgAttemptsPerQuestion: 0,
            coverage: 0,
            recentCorrectness: 0,
            totalAttempts: 0,
            totalTimeSpentMinutes: 0
        } as SummaryMetrics
    });

    const { allQuestionRows, exportFileName, exportRows, objectiveOptions, selectedObjectives, sort, summaryMetrics } = state;

    const { createToast } = useNotification();
    const { formatMessage } = useIntl();

    // Filter rows based on selected objectives (visual filter only — doesn't affect summary)
    const filteredRows = useMemo(() => {

        if (objectiveOptions.length === 0) return allQuestionRows;
        if (selectedObjectives.length === 0) return [];
        if (selectedObjectives.length === objectiveOptions.length) return allQuestionRows;
        const selectedValues = new Set(selectedObjectives.map(o => o.value));
        return allQuestionRows.filter(row => row.objectives.some(obj => selectedValues.has(obj))
        );

    }, [allQuestionRows, selectedObjectives, objectiveOptions]);

    const sortedData = useMemo(() => orderBy(filteredRows, sort), [filteredRows, sort]);
    const displayData = useMemo(() => sortedData.slice(0, MAX_DISPLAY_ROWS), [sortedData]);

    // Aggregate raw question-attempt data into per-question rows and summary metrics
    const aggregateData = useCallback((data: any[]) => {

        const allQuestionIds = new Set(data.map((d: any) => d.QuestionId));
        const totalQuestionsInScope = allQuestionIds.size;

        // Group attempts by question
        const questionMap: Record<number, any[]> = {};
        data.forEach((item: any) => {

            const qId = item.QuestionId;
            if (!questionMap[qId]) questionMap[qId] = [];
            questionMap[qId].push(item);

        });

        const questionRows: QuestionRow[] = Object.entries(questionMap).map(([, attempts]) => {

            const first = attempts[0];
            const questionName = first.QuestionName || `Question ${first.QuestionId}`;
            const projectName = first.ProjectName;
            const displayName = projectName
                ? `${projectName} ${questionName} (${first.QuestionId})`
                : `${questionName} (${first.QuestionId})`;

            // Collect objectives for this question (for filtering)
            const objSet = new Set<string>();
            attempts.forEach((a: any) => {

                if (a.ObjectiveName) objSet.add(a.ObjectiveName);

            });

            // Attempts count
            const totalAttempts = attempts.length;

            // Highest score: max of Score field across attempts
            const scores = attempts
                .map((a: any) => a.Score)
                .filter((s: any) => s !== null && s !== undefined);
            const highestScore = scores.length > 0
                ? Math.max(...scores) / 100 // Normalize from 0-100 to 0-1
                : null;

            // Most recent attempt and its score
            const mostRecentAttempt = attempts.reduce((latest: any, a: any) => {

                if (!a.TimeStamp) return latest;
                if (!latest || new Date(a.TimeStamp) > new Date(latest.TimeStamp)) return a;
                return latest;

            }, null);
            const mostRecentScore = mostRecentAttempt?.Score !== null && mostRecentAttempt?.Score !== undefined
                ? mostRecentAttempt.Score / 100 // Normalize from 0-100 to 0-1
                : null;

            // Time spent: deduplicate by ContentAccessId, then sum Testtotalminutesspent
            // But for per-question time, we need question-level time if available
            // Since we don't have per-question time, estimate: total test time / questions in that test
            // Actually, for a single student detail, we can just count unique test attempts time
            const seenAccessIds = new Set<number>();
            let totalTimeSpentMinutes = 0;
            attempts.forEach((a: any) => {

                if (a.ContentAccessId && !seenAccessIds.has(a.ContentAccessId)) {

                    seenAccessIds.add(a.ContentAccessId);
                    totalTimeSpentMinutes += a.Testtotalminutesspent || 0;

                }

            });

            const avgTimePerAttemptMinutes = totalAttempts > 0
                ? totalTimeSpentMinutes / totalAttempts
                : 0;

            // Last attempt date
            const lastAttempt = mostRecentAttempt?.TimeStamp
                ? new Date(mostRecentAttempt.TimeStamp)
                : null;

            return {
                attempts: totalAttempts,
                avgTimePerAttemptMinutes,
                displayName,
                highestScore,
                lastAttempt,
                mostRecentScore,
                objectives: Array.from(objSet),
                QuestionId: first.QuestionId,
                totalTimeSpentMinutes
            };

        });

        // Build objective options from all data
        const objMap = new Map<string, string>();
        data.forEach((item: any) => {

            if (item.ObjectiveName) {

                objMap.set(item.ObjectiveName, item.ObjectiveName);

            }

        });
        const options: ObjectiveOption[] = Array.from(objMap.entries())
            .map(([name]) => ({ name, value: name }))
            .sort((a, b) => a.name.localeCompare(b.name));
        setState({ objectiveOptions: options });
        setState({ selectedObjectives: options }); // Default: all selected

        // Summary metrics (always from full dataset, not filtered by objectives)
        const totalAttempts = data.length;
        const uniqueQuestionsAttempted = allQuestionIds.size;
        const coverage = totalQuestionsInScope > 0
            ? (uniqueQuestionsAttempted / totalQuestionsInScope) * 100
            : 0;

        // Attained: questions answered correctly at least once
        const correctQuestionIds = new Set(
            data.filter((a: any) => a.Correct?.Share === '1').map((a: any) => a.QuestionId)
        );
        const attained = totalQuestionsInScope > 0
            ? (correctQuestionIds.size / totalQuestionsInScope) * 100
            : 0;

        // Recent correctness: for each question, check if most recent attempt was correct
        const questionLatestMap: Record<number, any> = {};
        data.forEach((a: any) => {

            const qId = a.QuestionId;
            if (!questionLatestMap[qId] || new Date(a.TimeStamp) > new Date(questionLatestMap[qId].TimeStamp)) {

                questionLatestMap[qId] = a;

            }

        });
        const recentCorrectCount = Object.values(questionLatestMap)
            .filter((a: any) => a.Correct?.Share === '1').length;
        const recentCorrectness = totalQuestionsInScope > 0
            ? (recentCorrectCount / totalQuestionsInScope) * 100
            : 0;

        const avgAttemptsPerQuestion = totalQuestionsInScope > 0
            ? totalAttempts / totalQuestionsInScope
            : 0;

        // Total time: deduplicate by ContentAccessId across all attempts
        const seenAccessIds = new Set<number>();
        let totalTimeSpentMinutes = 0;
        data.forEach((a: any) => {

            if (a.ContentAccessId && !seenAccessIds.has(a.ContentAccessId)) {

                seenAccessIds.add(a.ContentAccessId);
                totalTimeSpentMinutes += a.Testtotalminutesspent || 0;

            }

        });

        setState({
            summaryMetrics: {
                attained,
                avgAttemptsPerQuestion,
                coverage,
                recentCorrectness,
                totalAttempts,
                totalTimeSpentMinutes
            }
        });

        setState({ allQuestionRows: questionRows });

    }, []);

    // Sorting
    const getSortableValue = useCallback((item: any, field?: string) => {

        if (!field) return null;
        if (field === 'lastAttempt') return item[field] ? new Date(item[field]).getTime() : null;
        return item[field];

    }, []);

    const handleSortChange = useCallback((event: GridSortChangeEvent) => {

        createSortChangeHandler(event, sort, val => setState({ sort: val }), getSortableValue);

    }, [sort, getSortableValue]);

    // Export
    const buildExport = useCallback(() => sortedData.map((row: QuestionRow) => ({

        [formatMessage({ id: 'attempts.ae7c8eee', defaultMessage: 'Attempts' })]: row.attempts,
        [formatMessage({ id: 'avg_time_attempt.c6fd1ca6', defaultMessage: 'Avg Time / Attempt' })]: calculateHourMinFormat(Math.round(row.avgTimePerAttemptMinutes)),
        [formatMessage({ id: 'highest_score.e8d57b6d', defaultMessage: 'Highest Score' })]: formatScore(row.highestScore),
        [formatMessage({ id: 'last_attempt.23c63d91', defaultMessage: 'Last Attempt' })]: row.lastAttempt ? formatDate(row.lastAttempt) : '---',
        [formatMessage({ id: 'most_recent_score.7acc5c70', defaultMessage: 'Most Recent Score' })]: formatScore(row.mostRecentScore),
        [formatMessage({ id: 'question.e7d7defa', defaultMessage: 'Question' })]: row.displayName,
        [formatMessage({ id: 'total_time_spent.88b400d6', defaultMessage: 'Total Time Spent' })]: calculateHourMinFormat(Math.round(row.totalTimeSpentMinutes))

    })), [sortedData, formatMessage]);

    const handleExportCSV = useCallback(() => {

        const reportName = reportSections[0]?.Title || 'StudentTestDetailReport';
        const fileName = buildExportFileName('Report', reportName, 'csv');
        setState({ exportFileName: fileName });
        setState({ exportRows: buildExport() });
        setTimeout(() => document.getElementById('csvLink72')?.click(), 0);

    }, [reportSections, buildExport]);

    // Initialize on mount
    useEffect(() => {

        const allData = reportSections[0]?.Components[0]?.Results?.Data ?? [];
        const rawData = allData.filter((d: any) => !d.ContentAccessTypeDisplayName || d.ContentAccessTypeDisplayName === mode);
        aggregateData(rawData);

    }, [aggregateData, reportSections, mode]);

    // Toast for truncated results
    useEffect(() => {

        if (filteredRows.length > MAX_DISPLAY_ROWS) {

            createToast(
                formatMessage(
                    { id: 'showing_first_shown_records_of_total_ple.18a484b7', defaultMessage: 'Showing first {shown} records of {total}. Please refine your filters to narrow the results.' },
                    { shown: MAX_DISPLAY_ROWS, total: filteredRows.length }
                ),
                'warning'
            );

        }

    }, [filteredRows]);

    // Summary card definitions
    const summaryCards = useMemo(() => [
        {
            label: formatMessage({ id: 'question_coverage.47d009b6', defaultMessage: 'Question Coverage' }),
            tooltip: formatMessage({ id: 'percentage_of_questions_attempted_at_lea.af8e7244', defaultMessage: 'Percentage of questions attempted at least once by the student' }),
            value: `${Math.round(summaryMetrics.coverage)}%`
        },
        {
            label: formatMessage({ id: 'attained.704661b2', defaultMessage: 'Attained' }),
            tooltip: formatMessage({ id: 'percentage_of_questions_answered_correct.ba49ce93', defaultMessage: 'Percentage of questions answered correctly at least once by this student' }),
            value: `${Math.round(summaryMetrics.attained)}%`
        },
        {
            label: formatMessage({ id: 'recent_correctness.2e3a499a', defaultMessage: 'Recent Correctness' }),
            tooltip: formatMessage({ id: 'percent_of_questions_where_this_students.2ae155c3', defaultMessage: "Percent of questions where this student's most recent submitted attempt was correct" }),
            value: `${Math.round(summaryMetrics.recentCorrectness)}%`
        },
        {
            label: formatMessage({ id: 'total_attempts.0d55f966', defaultMessage: 'Total Attempts' }),
            tooltip: formatMessage({ id: 'total_number_of_submitted_question_attem.80f112cd', defaultMessage: 'Total number of submitted question attempts by this student within the selected scope' }),
            value: summaryMetrics.totalAttempts.toString()
        },
        {
            label: formatMessage({ id: 'avg_attempts_question.64637c60', defaultMessage: 'Avg Attempts / Question' }),
            tooltip: formatMessage({ id: 'average_number_of_submitted_attempts_per.451c2944', defaultMessage: 'Average number of submitted attempts per question within the selected scope' }),
            value: summaryMetrics.avgAttemptsPerQuestion.toFixed(1)
        },
        {
            label: formatMessage({ id: 'total_time_spent.88b400d6', defaultMessage: 'Total Time Spent' }),
            tooltip: formatMessage({ id: 'total_time_spent_across_all_submitted_at.cf99a475', defaultMessage: 'Total time spent across all submitted attempts by this student within the selected scope' }),
            value: calculateHourMinFormat(Math.round(summaryMetrics.totalTimeSpentMinutes))
        }
    ], [summaryMetrics, formatMessage]);

    return (
        <>
            {/* Header Summary — Student Test Engagement */}
            <Container fluid className="px-0 mb-4">
                <h5 className="fw-bold mb-3">
                    <FormattedMessage defaultMessage="Student Test Engagement" id="student_test_engagement.408db61f" />
                </h5>
                <Row className="g-3 mx-0">
                    {summaryCards.map(metric => (
                        <Col key={metric.label} lg="2" md="4" sm="4" xs="6">
                            <Card className="p-3 shadow-sm h-100 text-center">
                                <ReportToolTip id={`summary-${metric.label}`} tooltip={metric.tooltip} trigger="hover">
                                    <div className="fw-semibold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                                        {metric.label}
                                    </div>
                                    <div className="fw-bold fs-3">
                                        {metric.value}
                                    </div>
                                </ReportToolTip>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Container>

            {/* Table */}
            <Card>
                <CSVLink
                    className="d-none"
                    data={exportRows}
                    filename={exportFileName}
                    id="csvLink72"
                    type="button"
                >
                </CSVLink>
                <div className="p-3 d-flex justify-content-between">
                    <div className="d-flex">
                        {objectiveOptions.length > 0 && (
                            <GMetrixDropdown
                                filterable
                                multiSelect
                                dataItemKey="value"
                                dataItems={objectiveOptions}
                                label={formatMessage({ id: 'show_questions_for_objectives.77d12c2f', defaultMessage: 'Show Questions For Objectives' })}
                                onChange={e => setState({ selectedObjectives: e as ObjectiveOption[] })}
                                style={{ maxWidth: '300px' }}
                                textField="name"
                                value={selectedObjectives}
                            />
                        )}
                    </div>
                    <div className="mt-4">
                        <GMetrixButton onClick={handleExportCSV} variant="primary">
                            <FormattedMessage defaultMessage="Export to CSV" id="export_to_csv.e1de1359" />
                        </GMetrixButton>
                    </div>
                </div>
                <div style={{ maxHeight: 'calc(100vh - 70px - 24px - 105px)', overflowX: 'auto', overflowY: 'auto' }}>
                    <Table bordered hover className="kendo-table sticky-table-header mb-0">
                        <thead>
                            <tr>
                                <th
                                    className="text-start"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'displayName' }] })}
                                    style={{ cursor: 'pointer', left: 0, minWidth: 280, position: 'sticky', zIndex: 1 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-question" tooltip={formatMessage({ id: 'question_included_within_the_selected_te.701137f2', defaultMessage: 'Question included within the selected Test(s)' })} trigger="hover">
                                            <span className="d-flex justify-content-start">
                                                <FormattedMessage defaultMessage="Question" id="question.e7d7defa" />
                                            </span>
                                        </ReportToolTip>,
                                        'displayName',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'attempts' }] })}
                                    style={{ cursor: 'pointer', minWidth: 120 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-attempts" tooltip={formatMessage({ id: 'total_number_of_submitted_attempts_by_th.542fadd8', defaultMessage: 'Total number of submitted attempts by this student for the question' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Attempts" id="attempts.ae7c8eee" />
                                            </span>
                                        </ReportToolTip>,
                                        'attempts',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'highestScore' }] })}
                                    style={{ cursor: 'pointer', minWidth: 140 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-highestScore" tooltip={formatMessage({ id: 'highest_score_achieved_by_this_student_f.c696f960', defaultMessage: 'Highest score achieved by this student for this question within the selected scope' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Highest Score" id="highest_score.e8d57b6d" />
                                            </span>
                                        </ReportToolTip>,
                                        'highestScore',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'mostRecentScore' }] })}
                                    style={{ cursor: 'pointer', minWidth: 160 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-recentScore" tooltip={formatMessage({ id: 'score_from_the_most_recent_submitted_att.ab083ef5', defaultMessage: 'Score from the most recent submitted attempt for this question within the selected scope' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Most Recent Score" id="most_recent_score.7acc5c70" />
                                            </span>
                                        </ReportToolTip>,
                                        'mostRecentScore',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'avgTimePerAttemptMinutes' }] })}
                                    style={{ cursor: 'pointer', minWidth: 160 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-avgTime" tooltip={formatMessage({ id: 'average_time_spent_per_submitted_attempt.8fa78209', defaultMessage: 'Average time spent per submitted attempt for this question within the selected scope' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Avg Time / Attempt" id="avg_time_attempt.c6fd1ca6" />
                                            </span>
                                        </ReportToolTip>,
                                        'avgTimePerAttemptMinutes',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'totalTimeSpentMinutes' }] })}
                                    style={{ cursor: 'pointer', minWidth: 150 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-totalTime" tooltip={formatMessage({ id: 'total_time_spent_across_all_submitted_at.c9d501f0', defaultMessage: 'Total time spent across all submitted attempts for this question within the selected scope' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Total Time Spent" id="total_time_spent.88b400d6" />
                                            </span>
                                        </ReportToolTip>,
                                        'totalTimeSpentMinutes',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'lastAttempt' }] })}
                                    style={{ cursor: 'pointer', minWidth: 150 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-lastAttempt" tooltip={formatMessage({ id: 'most_recent_submitted_attempt_date_for_t.f5caa69d', defaultMessage: 'Most recent submitted attempt date for this question within the selected scope' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Last Attempt" id="last_attempt.23c63d91" />
                                            </span>
                                        </ReportToolTip>,
                                        'lastAttempt',
                                        sort
                                    )}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayData.map((question: QuestionRow, rowIndex: number) => (
                                <tr key={`${question.QuestionId}-${rowIndex}`}>
                                    <td style={{ backgroundColor: 'white', left: 0, overflowX: 'hidden', position: 'sticky', zIndex: 1 }}>
                                        <ReportToolTip copyText={question.displayName} id={`q-${question.QuestionId}`} tooltip={question.displayName} trigger="hover">
                                            <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {question.displayName}
                                            </span>
                                        </ReportToolTip>
                                    </td>
                                    <td className="text-center">
                                        {question.attempts}
                                    </td>
                                    <td className="text-center">
                                        {formatScore(question.highestScore)}
                                    </td>
                                    <td className="text-center">
                                        {formatScore(question.mostRecentScore)}
                                    </td>
                                    <td className="text-center">
                                        {calculateHourMinFormat(Math.round(question.avgTimePerAttemptMinutes))}
                                    </td>
                                    <td className="text-center">
                                        {calculateHourMinFormat(Math.round(question.totalTimeSpentMinutes))}
                                    </td>
                                    <td className="text-center">
                                        {question.lastAttempt ? formatDate(question.lastAttempt) : '---'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </Card>
        </>
    );

};